(ns rlws.iot-cloud.frontend.uikit.table
  (:require
   [clojure.pprint :as pprint]
   [re-frame.core :as rf]
   [rlws.iot-cloud.frontend.site :as site]
   [rlws.iot-cloud.frontend.events :as events]
   [rlws.iot-cloud.frontend.subscriptions :as sub]
   [rlws.iot-cloud.frontend.navigation :as navigation]
   [rlws.iot-cloud.frontend.assets.components :as assets.components]
   [rlws.iot-cloud.frontend.uikit.utils :as utils]
   [reagent.core :as r]))

(defn pprint-str
  [x]
  (with-out-str (pprint/pprint x)))

(defn pprint-state
  [x]
  [:code
   {:style {:text-align "left"}}
   [:pre (pprint-str x)]])

(defn table-filters
  [{:keys [filters buttons] :as data} table-atom paginated-rows]
  (let [filters (:filters data)]
    (into
     [:div.table__dropdown-filters-container
      [:div.table__dropdown-filter-container
       [:div.dropdown-filter__label
        [:p "Search all"]]
       [:div.filter__search-all
        [:div.form-control-wrapper
         [:input.form-control
          {:type "text"
           :value (utils/filter-all-value @table-atom)
           :on-change #(utils/filter-all-on-change % table-atom)}]]]]]
     (conj
      (mapv
       (fn [{:keys [label column-key]}]
         ^{:key (str label column-key)}
         [utils/component-hide-show
          (fn [active? ref-button ref-dropdown]
            [:div.table__dropdown-filter-container
             [:div.dropdown-filter__label
              [:p label]]
             [:button.dropdown-filter__button
              {:ref ref-button}
              [:div.dropdown-filter__selected
               (utils/column-select-label @table-atom column-key)]
              [:i.fas.fa-chevron-down]]
             (into
              [:div
               {:ref ref-dropdown
                :class (if active?
                         "table__dropdown-filter--show"
                         "table__dropdown-filter--hide")}]
              (mapv
               (fn [[id value processed-value]]
                 ^{:key (str id value)}
                 [:div.dropdown-filter__element
                  {:on-click #(utils/column-select-filter-on-change table-atom column-key value processed-value)}
                  [:input.checkbox
                   {:type "checkbox"
                    :checked (utils/column-select-filter-value @table-atom column-key value processed-value)
                    :value value
                    :on-change #()}]
                  [:label processed-value]])
               (utils/column-select-filter-options data column-key)))])]) filters)
      (when filters
        [:div.table__dropdown-filter-container
         [:div.dropdown-filter__label
          ;; needed for spacing consistency
          [:p.opacity-0 "Clear"]]
         [:button.dropdown-filter__button
          {:class (when (utils/filters-unselected? @table-atom)
                    "btn--disabled")
           :on-click #(utils/column-filter-reset-all table-atom)}
          [:i.fas.fa-ban.color-primary.margin-right-xxs]
          [:p.dropdown-filter__selected "Clear Filters"]]]
        (when buttons
          [:<>
           (for [button buttons]
             ^{:key (hash button)}
             [:div.table__dropdown-filter-container
              [:div.dropdown-filter__label
               ;; needed for spacing consistency
               [:p.opacity-0 "&nbsp;"]]
              button])]))))))

(defn header-columns
  [data table-atom]
  (let [columns (utils/table-columns data @table-atom)
        active-sort-column (ffirst (get-in @table-atom [:utils :sort]))]
    [:thead.table-header
     (into [:tr.table-header__tr]
           (mapv
            (fn [{:keys [column-key column-name]}]
              ^{:key column-key}
              [:th.table-header__th
               {:class (when (= column-key active-sort-column)
                         "table-header__th--active-sort")
                :on-click #(utils/column-sort table-atom
                                              column-key)}
               [:div.table-header__th-content
                [:p.table-header__th-column column-name]
                [:i.fas
                 ;; sort table by column inc or dec order
                 {:class (utils/column-sort-icon @table-atom
                                                 column-key)}]]])
            columns))]))

(defn loading-table
  [data {:keys [rows cols]}]
  (let [columns (:columns data)
        table-atom (r/atom {:utils (dissoc data :columns :rows :filters)})]
    [:div.table__main
     [:table.table
      (if columns
        [header-columns data table-atom]
        [:thead
         [:tr
          (for [col (range cols)]
            ^{:key col}
            [:th [:span]])]])
      [:tbody.table__body
       (for [row (range rows)]
         ^{:key row}
         [:tr.loading
          (for [col (range (or (count columns) cols))]
            ^{:key col}
            [:td.td-loading-bar
             [:span.loading-bar__span]])])]]]))

(defn body-rows
  [data table-atom rows]
  (let [columns (utils/table-columns data @table-atom)]
    (if (and (nil? (:rows data))
             (seq? rows))
      [:tbody.table-body
       (for [n (range 10)]
         ^{:key n}
         [:tr.loading
          (for [col columns]
            ^{:key col}
            [:td.td-loading-bar
             [:span.loading-bar__span]])])]
      [:tbody.table-body
       (for [row rows]
         ^{:key row}
         [:tr.table__row
          (for [{:keys [column-key render-fn row-link]} columns]
            ^{:key (str row column-key)}
            [:td
             [:a.padding-sm.row-link
              (let [row-link (or row-link (:row-link data))]
                (merge {}
                       (when-let [href-fn (:href row-link)]
                         {:class "cursor-pointer"
                          :href  (href-fn row)})
                       (when-let [on-click-fn (:on-click row-link)]
                         {:class "cursor-pointer"
                          :on-click (fn [_] (on-click-fn row))})
                       (when-let [target (:target row-link)]
                         {:target target})))
              (if render-fn
                (render-fn row (column-key row))
                (column-key row))]])])])))

(defn scale-rows
  [data table-atom rows]
  (let [columns (utils/table-columns data @table-atom)]
    (if (and (nil? (:rows data))
             (seq? rows))
      [:tbody.table-body
       (for [n (range 10)]
         ^{:key n}
         [:tr.loading
          (for [col columns]
            ^{:key col}
            [:td.td-loading-bar
             [:span.loading-bar__span]])])]
      [:tbody.table-body
       (for [row rows]
         ^{:key row}
         [:tr.table__row
          (for [{:keys [column-key render-fn row-link]} columns]
            ^{:key (str row column-key)}
            [:td
             [:a.padding-sm.row-link 
                {:on-click (fn []
                             (let [iqubes @(rf/subscribe [::sub/complete-iqubes])
                                   iqube (first (filterv #(= (site/to-plain-id %) (:iqube row)) iqubes))]
                               (rf/dispatch [:rlws.iot-cloud.frontend.events/start-watch-alerts (:gateway row) (:number row) (:ip iqube)])
                               (rf/dispatch [:fetch/data [:alerts (:gateway row)] (site/resource-path :alerts) {:asset (:gateway row) :scale-number (:number row) :ip-address (:ip iqube)}])
                               (rf/dispatch [:fetch/data [:service-notes] (site/resource-path :service-notes) {:asset (:gateway row) :scale-number (:number row) :ip-address (:ip iqube)}])
                               (rf/dispatch [:fetch/data [:customer] (site/resource-path :customers (:customer row))])
                               (rf/dispatch [:fetch/data [:site] (site/resource-path :sites (:site row))])))
                 :href (navigation/id-route
                          :asset
                          {:asset (:gateway row)} {:iqube (:iqube row) :scale (site/to-plain-id row)})}
              (if render-fn
                (render-fn row (column-key row))
                (column-key row))]])])])))

(defn asset-body-rows 
  [data table-atom rows] 
  (let [columns (utils/table-columns data @table-atom)
        tabs @(rf/subscribe [::sub/site-asset-tab])
        iqube-tabs @(rf/subscribe [::sub/site-iqube-tab])
        iqubes @(rf/subscribe [::sub/complete-iqubes])
        scales @(rf/subscribe [::sub/complete-scales])
        live-assets @(rf/subscribe [::sub/current-scale-status])] 
    (if (and (nil? (:rows data))
             (seq? rows))
      [:tbody.asset-table-body
       (for [n (range 10)]
         ^{:key n}
         [:tr.loading
          (for [col columns]
            ^{:key col}
            [:td.td-loading-bar
             [:span.loading-bar__span]])])]
      [:tbody.asset-table-body
       (for [row rows]
         ^{:key row}
         [:tr.table__row
          (for [{:keys [column-key render-fn row-link]} columns]
            ^{:key (str row column-key)}
            [:td
             [:div.table__row-title
              [:a.padding-sm.row-link {:style {:cursor "auto"}}
               (if render-fn
                 (render-fn row (column-key row))
                 (column-key row))]
              [:div {:on-click (fn []
                                 (rf/dispatch [::events/set-site-asset-tab (:id row)])
                                 (rf/dispatch [::events/set-dialog :edit-asset])
                                 (rf/dispatch [:fetch/data [:asset] (site/resource-path :assets (site/to-plain-id row))]))
                     :style {:cursor "pointer"
                             :backgroundColor "transparent"}}
               [:img {:src "/img/icon-info.svg"}]]]
             [:button
              {:class "add-asset-button"
               :on-click (fn [] 
                           (rf/dispatch [:fetch/data [:asset] (site/resource-path :assets (:id row))])
                           (rf/dispatch [::events/set-dialog :iqube]))}
              [:span.desktop "+ Add iQUBE"]
              [:span.mobile "+"]] 
             (if (= tabs (:id row))
               [:div
                [:img.outside__chevron {:src "/img/table-up-chevron.svg"
                                        :on-click (fn []
                                                    (rf/dispatch [::events/set-site-asset-tab ""]))}]
                [:div.table__titles
                 [:div {:style {:width "20%"}}
                  [:div.asset__name
                   [:span "Name"]]]
                 [:div
                  [:div.asset__status
                   [:span "Status"]]]
                 [:div.hidden-column
                  [:div.asset__serial
                   [:span "SN / ID"]]]
                 [:div.hidden-column
                  [:div.asset__board
                   [:span "Boards / Cells"]]]
                 [:div.hidden-column
                  [:div.asset__location
                   [:span "Location"]]]
                 [:button
                  {:class "add-asset-inside-button"}
                  [:span.desktop "+ Add Scale"]
                  [:span.mobile "+"]]

                 [:img.hidden__img {:src "/img/table-down-chevron.svg"}]]
                (for [iqube iqubes :when (= (:gateway iqube) (:id row))]
                  ^{:key (site/to-plain-id iqube)}
                    [:div.table__display
                     [:div.iqubes__display
                      [:a
                       {:href (navigation/id-route
                               :iqube-detail
                               {:iqube-detail (site/to-plain-id iqube)})
                        :on-click (fn []
                                    (rf/dispatch [:fetch/data [:asset] (site/resource-path :assets (:id row))]))}
                       [:div.asset__name
                        [:div {:style {:display "flex"
                                       :flex-direction "row"}}
                         [:img.hidden__img {:src "/img/icon-accessories.svg" :style {:height "22px"}}]
                         [:span {:style {:margin-top "3px"
                                         :margin-left "5px"}} (:name iqube)]]]]
                      [:div
                       [:div.asset__status]]
                      [:div.hidden-column
                       [:div.asset__serial
                        [:span (:serial iqube)]]]
                      [:div.hidden-column
                       [:div.asset__board
                        (if (:boards iqube) [:span (str (:boards iqube) " Boards")] [:span "-"])]]
                      [:div.hidden-column
                       [:div.asset__location
                        [:span (:location iqube)]]]
                      [:button
                       {:class "add-asset-inside-button"
                        :on-click (fn []
                                    (rf/dispatch [:fetch/data [:iqube] (site/resource-path :iqubes (site/to-plain-id iqube))])
                                    (rf/dispatch [:fetch/data [:asset] (site/resource-path :assets (site/to-plain-id row))])
                                    (rf/dispatch [::events/set-selected-scale-type "Other"])
                                    (rf/dispatch [::events/set-dialog :scale]))}
                       [:span.desktop "+ Add Scale"]
                       [:span.mobile "+"]]
                      (if (= iqube-tabs (site/to-plain-id iqube))
                        [:img.inside__chevron {:src "/img/icon-chevron-up-white.svg"
                                               :on-click (fn []
                                                           (rf/dispatch [::events/set-site-iqube-tab ""])
                                                           (rf/dispatch [:fetch/data [:iqube] (site/resource-path :iqubes nil)]))}]
                        [:img.inside__chevron {:src "/img/icon-chevron-down-white.svg"
                                               :on-click (fn []
                                                           (rf/dispatch [::events/set-site-iqube-tab (site/to-plain-id iqube)])
                                                           (rf/dispatch [:fetch/data [:iqube] (site/resource-path :iqubes (site/to-plain-id iqube))]))}])]
                     (if (= iqube-tabs (site/to-plain-id iqube))
                       (for [scale scales :when (= (:iqube scale) (site/to-plain-id iqube))]
                         ^{:key (site/to-plain-id scale)}
                           [:div.scale__display
                            [:a
                             {:href (navigation/id-route
                                     :asset
                                     {:asset (site/to-plain-id row)} {:iqube (site/to-plain-id iqube) :scale (site/to-plain-id scale)})
                              :on-click (fn []
                                          (rf/dispatch [:rlws.iot-cloud.frontend.events/start-watch-alerts (:id row) (:number scale) (:ip iqube)])
                                          (rf/dispatch [:fetch/data [:alerts (:id row)] (site/resource-path :alerts) {:asset (:id row) :scale-number (:number scale) :ip-address (:ip iqube)}])
                                          (rf/dispatch [:fetch/data [:service-notes] (site/resource-path :service-notes) {:asset (:id row) :scale-number (str (:number scale)) :ip-address (:ip iqube)}])
                                          (rf/dispatch [:fetch/data [:customer] (site/resource-path :customers (:customer scale))])
                                          (rf/dispatch [:fetch/data [:site] (site/resource-path :sites (:site scale))]))}
                             [:div.asset__name
                              [:div {:style {:display "flex"
                                             :flex-direction "row"}}
                               [utils/scale-icon (:category scale)]
                               [:span {:style {:margin-top "3px"
                                               :margin-left "5px"}} (:name scale)]]]]
                            [:div 
                             [:div.asset__status
                              (if (not (contains? live-assets (str (:gateway scale) " " (:number scale) " " (:ip iqube))))
                                [assets.components/asset-status nil]
                                (for [asset live-assets :when (= (first asset) (str (:gateway scale) " " (:number scale) " " (:ip iqube)))]
                                  ^{:key asset}
                                  [assets.components/asset-status (second asset)]))]]
                            [:div.hidden-column
                             [:div.asset__serial
                              [:span (:number scale)]]]
                            [:div.hidden-column
                             [:div.asset__board
                              (if (:load-cell scale) [:span (str (:load-cell scale) " Cells")] [:span "-"])]]
                            [:div.hidden-column
                             [:div.asset__location
                              [:span (:location scale)]]]
                            [:button
                             {:class "add-asset-inside-scale-button"}
                             [:span "+ Add Scale"]
                             [:span.mobile "+"]]
                            [:img.inside__scale {:src "/img/table-down-chevron.svg"}]]))])]
               [:img.outside__chevron {:src "/img/table-down-chevron.svg"
                                       :on-click (fn []
                                                   (rf/dispatch [::events/set-site-asset-tab (:id row)])
                                                   (rf/dispatch [:fetch/data [:asset] (site/resource-path :assets (:id row))]))}])])])])))

(defn filter-all
  [table-atom]
  [:div.top__filter-all
   [:input.input.input--side-icons.input__no-borders
    {:value (utils/filter-all-value @table-atom)
     :on-change #(utils/filter-all-on-change % table-atom)}]
   [:span.input__icon.input__left-icon
    [:i.fas.fa-search]]
   (when (not-empty (utils/filter-all-value @table-atom))
     [:span.input__icon.input__right-icon.input__icon--clickable
      {:on-click #(utils/filter-all-reset table-atom)}
      [:i.fas.fa-times]])])

(defn pagination
  [table-atom processed-rows]
  [:table.table__pagination
   [:tfoot
    [:tr
     [:td.table__pagination-td
      [utils/select
       (fn [active? ref-button ref-dropdown]
         [:div.pagination__select
          [:button.btn.btn--subtle
           {:ref ref-button}
           (utils/pagination-rows-per-page @table-atom)
           [:i.fas.fa-caret-down.margin-left-xxs]]
          [:div.select
           {:ref ref-dropdown
            :class (if active?
                     "select--show"
                     "select--hide")}
           (let [on-click #(utils/pagination-rows-per-page-on-change % table-atom)]
             [:<>
              [:option.option
               {:value "10"
                :on-click on-click}
               (str "10" " rows")]
              [:option.option
               {:value "50"
                :on-click on-click}
               (str "50" " rows")]
              [:option.option
               {:value "100"
                :on-click on-click}
               (str "100" " rows")]])]])]
      [:div.pagination__info
       (utils/pagination-current-and-total-pages @table-atom
                                                 processed-rows)]
      [:div.pagination__arrow-group
       [:div.pagination__arrow-nav
        {:class (when (<= (utils/pagination-current-page @table-atom) 0)
                  "pagination__arrow-nav--disabled")
         :on-click #(utils/pagination-dec-page table-atom)}
        [:i.fas.fa-chevron-left]]
       [:div.pagination__arrow-nav
        {:class (when (utils/pagination-rows-exhausted? @table-atom
                                                        processed-rows)
                  "pagination__arrow-nav--disabled")
         :on-click #(utils/pagination-inc-page table-atom
                                               processed-rows)}
        [:i.fas.fa-chevron-right]]]]]]])

(defn table
  [data]
  (let [table-atom (r/atom {:utils (dissoc data :columns :rows :filters)})]
    (fn [data]
      (let [[processed-rows paginated-rows] (utils/process-rows data @table-atom)
            debug? @(rf/subscribe [:debug?])]
        [:<>
         (when debug? [:code.debug "Debug on (ctrl+del to turn off): Keys available = " (str (keys (first (:rows data))))])
         [:div.uikit-table
          [table-filters data table-atom paginated-rows]
          [:div.table-container
           [:table.table
            [header-columns data table-atom]
            [body-rows data table-atom paginated-rows]]]
          (when (nil? paginated-rows)
            [:div.no-data "Nothing to show"])
          (when (seq paginated-rows)
            [pagination table-atom processed-rows])]]))))

(defn scale-table
  [data]
  (let [table-atom (r/atom {:utils (dissoc data :columns :rows :filters)})]
    (fn [data]
      (let [[processed-rows paginated-rows] (utils/process-rows data @table-atom)
            debug? @(rf/subscribe [:debug?])]
        [:<>
         (when debug? [:code.debug "Debug on (ctrl+del to turn off): Keys available = " (str (keys (first (:rows data))))])
         [:div.uikit-table
          [table-filters data table-atom paginated-rows]
          [:div.table-container
           [:table.table
            [header-columns data table-atom]
            [scale-rows data table-atom paginated-rows]]]
          (when (nil? paginated-rows)
            [:div.no-data "Nothing to show"])
          (when (seq paginated-rows)
            [pagination table-atom processed-rows])]]))))

(defn asset-table
  [data] 
  (let [table-atom (r/atom {:utils (dissoc data :columns :rows :filters)})]
    (fn [data]
      (let [[processed-rows paginated-rows] (utils/process-rows data @table-atom)
            debug? @(rf/subscribe [:debug?])]
        [:<>
         (when debug? [:code.debug "Debug on (ctrl+del to turn off): Keys available = " (str (keys (first (:rows data))))])
         [:div.asset-uikit-table 
          [table-filters data table-atom paginated-rows]
          [:div.table-container
           [:table.table
            [asset-body-rows data table-atom paginated-rows]]]
          (when (nil? paginated-rows)
            [:div.no-asset-data
             [:span.description "There must be an onboarded Gateway before any iQUBEs, Secondary Boxes or Scales can be added."]
             [:span.clickable-text {:on-click (fn []
                                                (rf/dispatch [::events/set-dialog :asset]))} 
              "+ Add a Gateway"]])
          (when (seq paginated-rows)
            [pagination table-atom processed-rows])]]))))

(defn asset-component-table 
  [data]
  (let [table-atom (r/atom {:utils (dissoc data :columns :rows :filters)})]))